import React, { useState, useEffect } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    createDefaultOrganization,
    createClassItems,
    saveToLocalStorage,
    saveToFirebase,
    loadFromLocalStorage,
    loadFromFirebase,
} from '../services/classOrganizationService';
import { auth } from '../firebase';
import './ClassOrganizer.css';

// Sortable Item Component
function SortableItem({ id, item }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`class-item ${item.type}`}
        >
            <div className="class-item-icon">
                {item.type === 'google' ? '📚' : '🖼️'}
            </div>
            <div className="class-item-content">
                <div className="class-item-name">{item.name}</div>
                <div className="class-item-assignments">
                    {item.assignmentCount} assignment{item.assignmentCount !== 1 ? 's' : ''}
                </div>
            </div>
        </div>
    );
}

// Droppable Container Component
function DroppableContainer({ id, title, items, allItems }) {
    const { setNodeRef } = useDroppable({ id });

    return (
        <div ref={setNodeRef} className="droppable-container">
            <h3 className="container-title">{title}</h3>

            <SortableContext
                items={items}
                strategy={verticalListSortingStrategy}
            >
                <div className="container-items">
                    {items.length === 0 ? (
                        <div className="empty-state">Drop classes here</div>
                    ) : (
                        items.map(itemId => {
                            const item = allItems.find(i => i.id === itemId);
                            return item ? (
                                <SortableItem key={itemId} id={itemId} item={item} />
                            ) : null;
                        })
                    )}
                </div>
            </SortableContext>
        </div>
    );
}

// Main ClassOrganizer Component
export default function ClassOrganizer({ googleClasses, canvasEvents, canvasCourses }) {
    const [organization, setOrganization] = useState(createDefaultOrganization());
    const [allClassItems, setAllClassItems] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Initialize class items and load saved organization
    useEffect(() => {
        const items = createClassItems(googleClasses, canvasEvents, canvasCourses);
        setAllClassItems(items);

        if (
            items.length > 0 &&
            items.every(item => item.id && !item.id.includes("undefined"))
        ) {
            if (!hasLoaded) {
                loadOrganization(items);
                setHasLoaded(true);
            }
        }
    }, [googleClasses, canvasEvents, canvasCourses, hasLoaded]);

    // Load organization from Firebase or localStorage
    const loadOrganization = async (items) => {
        console.log('Loading class organization...');

        const defaults = createDefaultOrganization();

        const firebaseOrg = await loadFromFirebase();
        if (firebaseOrg) {
            setOrganization({
                ...defaults,
                ...firebaseOrg
            });
            return;
        }

        const localOrg = loadFromLocalStorage();
        if (localOrg) {
            setOrganization({
                ...defaults,
                ...localOrg
            });
            return;
        }

        // First-time user
        setOrganization({
            ...defaults,
            unassigned: items.map(item => item.id)
        });
    };

    // Save organization
    const saveOrganization = (newOrg) => {
        setOrganization(newOrg);
        saveToLocalStorage(newOrg);
        if (auth.currentUser) {
            saveToFirebase(newOrg);
        }
    };

    // Find which container an item is in
    const findContainer = (id) => {
        if (id in organization) {
            return id;
        }

        return Object.keys(organization).find((key) =>
            organization[key].includes(id)
        );
    };

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    // Handle drag over
    const handleDragOver = (event) => {
        const { active, over } = event;

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId) || overId;

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return;
        }

        setOrganization((prev) => {
            const activeItems = prev[activeContainer];
            const overItems = prev[overContainer];

            // const activeIndex = activeItems.indexOf(activeId);
            const overIndex = overItems.indexOf(overId);

            let newIndex;
            if (overId in prev) {
                newIndex = overItems.length + 1;
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            return {
                ...prev,
                [activeContainer]: activeItems.filter((item) => item !== activeId),
                [overContainer]: [
                    ...overItems.slice(0, newIndex),
                    activeId,
                    ...overItems.slice(newIndex, overItems.length),
                ],
            };
        });
    };

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;

        setActiveId(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId) || overId;

        if (!activeContainer || !overContainer) return;

        setOrganization((prev) => {
            let newOrg = { ...prev };

            // If moved across containers, state already updated in dragOver
            // Just persist what we currently have
            saveOrganization(newOrg);
            return newOrg;
        });
    };

    const activeItem = activeId ? allClassItems.find((item) => item.id === activeId) : null;

    const containers = [
        { id: 'period1', title: 'Period 1' },
        { id: 'period2', title: 'Period 2' },
        { id: 'period3', title: 'Period 3' },
        { id: 'period4', title: 'Period 4' },
        { id: 'period5', title: 'Period 5' },
        { id: 'period6', title: 'Period 6' },
        { id: 'period7', title: 'Period 7' },
        { id: 'other', title: 'Other' },
        { id: 'unassigned', title: 'Unassigned' }
    ];


    return (
        <div className="class-organizer">
            <h2>Organize Your Classes</h2>
            <p className="organizer-description">
                Drag and drop your classes into the appropriate periods
            </p>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <div className="organizer-grid">
                    {containers.map((container) => (
                        <DroppableContainer
                            key={container.id}
                            id={container.id}
                            title={container.title}
                            items={organization[container.id] || []}
                            allItems={allClassItems}
                        />
                    ))}
                </div>

                <DragOverlay>
                    {activeItem ? (
                        <div className={`class-item ${activeItem.type} dragging`}>
                            <div className="class-item-icon">
                                {activeItem.type === 'google' ? '📚' : '🖼️'}
                            </div>
                            <div className="class-item-content">
                                <div className="class-item-name">{activeItem.name}</div>
                                <div className="class-item-assignments">
                                    {activeItem.assignmentCount} assignment{activeItem.assignmentCount !== 1 ? 's' : ''}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
}